import styled, { css } from 'styled-components';
import { rem, rgba } from '@helpers/style';

export const FieldGroup = styled.div`
	position: relative;
	margin-bottom: 35px;

	&:after {
		content: '';
		clear: both;
		display: block;
	}

	${(p) =>
    p.$align &&
    css`
			text-align: ${p.$align};
		`}

	.loader {
		opacity: 0;
	}
	&.loading {
		.loader {
			opacity: 1;
		}

		.icon {
			transform: scale(0);
		}
	}
`;

export const StyledInput = styled.input`
  float: right;
  width: 100%;
  height: 45px;
  font-size: ${(p) => rem(16, p.theme.fontSize.base)};
  padding: 5px;
  direction: rtl;
  background-color: ${(p) => p.theme.color.fgColor};
  appearance: none;

  ${(p) =>
    p.$outline
      ? css`
					border: 1px solid ${p.theme.color.grayLight};
					border-radius: ${p.theme.button.radius}px;
			  `
      : css`
					border: none;
					border-bottom: 1px solid ${p.theme.color.grayLight};
			  `}

  ${(p) =>
    p.$error &&
    css`
			border-color: ${p.theme.color.danger};
			color: ${p.theme.color.danger};
		`}

  ${(p) =>
    p.$done &&
    css`
        border-color: ${p.theme.color.success};
        color: ${p.theme.color.success};
      }
    `}

  ${(p) =>
    p.$align
      ? css`
					text-align: ${p.$align};
			  `
      : css`
					text-align: right;
			  `}
        
  ${(p) =>
    p.$ltr &&
    css`
			direction: ltr;
		`}

  &[disabled] {
    outline: none;
    user-select: none;
    color: ${(p) => p.theme.color.grayMedium};
    opacity: 1;
    -webkit-text-fill-color: ${(p) => p.theme.color.grayMedium};

    ~ label span {
      color: ${(p) => p.theme.color.grayMedium} !important;
    }
  }

  ${(p) =>
    (p.$hasValue || p.$hasFocus) &&
    css`
      & + label {
        span {
          ${p.$outline
        ? css`
									transform: translate(0, -8px);
									background-color: #fff;
									padding: 0 8px;
									width: auto;
							  `
        : css`
									transform: translate(0, -13px);
							  `
      }

          font-size: ${rem(11, p.theme.fontSize.base)};
          color: ${p.theme.color.secondary};

          ${p.$done &&
      css`
							color: ${p.theme.color.success};
						`
      };
          ${p.$error &&
      css`
							color: ${p.theme.color.danger};
						`
      };
        }
      }
    `}
`;

export const Textarea = styled(StyledInput)`
	min-height: 80px;
	resize: vertical;
`;

export const Label = styled.label`
  ${(p) => p.$hasValue}
  position: absolute;
  top: 0;
  left: 5px;
  right: 5px;
  bottom: 0;
  pointer-events: none;
  user-select: none;
  display: block;

  span {
    position: relative;
    float: right;
    width: ${(p) => (p.$outline ? 'auto' : '100%')};
    pointer-events: none;
    transform: translate(0, 50%);
    white-space: nowrap;
    transition: ${(p) => p.theme.transition.all};

    ${(p) =>
    p.$outline &&
    css`
				margin: 0 5px;
			`}

    color: ${(p) => rgba(p.theme.color.textColor, 0.4)};
    ${(p) =>
    p.$done &&
    css`
				color: ${p.theme.color.success};
			`};
    ${(p) =>
    p.$error &&
    css`
				color: ${p.theme.color.danger};
			`};

    ${(p) =>
    p.$align
      ? css`
						text-align: ${p.$align};
				  `
      : css`
						text-align: right;
				  `}
  }

  &:before {
    position: absolute;
    left: 0;
    height: 2px;
    bottom: 0;
    width: 100%;
    content: '';
    transform: scaleX(0);
    transition: ${(p) => p.theme.transition.all};
    background: ${(p) => p.theme.color.secondary};
    ${(p) =>
    p.$done &&
    css`
				background-color: ${p.theme.color.success};
			`};
    ${(p) =>
    p.$error &&
    css`
				background-color: ${p.theme.color.danger};
			`};
  }
`;

export const Hint = styled.span`
	font-size: ${(p) => rem(12, p.theme.fontSize.base)};
	position: absolute;
	top: 100%;
	right: 0;
	left: 0;
	padding: 5px 5px 0;
	color: ${(p) => p.theme.color[p.$type === 'error' ? 'danger' : 'grayMedium']};
	direction: rtl;

	${(p) =>
    p.$align
      ? css`
					text-align: ${p.$align};
			  `
      : css`
					text-align: right;
			  `}
`;

export const StyledIcon = styled.div`
  .icon {
    position: absolute;
    left: 5px;
    padding: 2px;
    top: 50%;
    font-size: ${(p) => rem(18, p.theme.fontSize.base)};
    margin-top: -14px;
    transition: transform 400ms ${(p) => p.theme.transition.function};
    background-color: ${(p) => p.theme.color.fgColor};
    transform: scale(${(p) => (p.$done || p.$error || p.$icon ? 1 : 0)});

    ${(p) =>
    p.disabled &&
    css`
				color: ${p.theme.color.grayMedium};
			`}

    ${(p) =>
    p.$done &&
    css`
				color: ${p.theme.color.success};
			`}

    ${(p) =>
    p.$error &&
    css`
				color: ${p.theme.color.danger};
			`}
  }
`;

export const RadioCheckboxWrapper = styled.div`
	position: relative;

	&:hover {
		label {
			span {
				border-color: ${(p) => p.theme.color.primary};
			}
		}
	}
`;

export const RadioCheckbox = styled.input`
	position: absolute;
	opacity: 0;
	pointer-events: none;

	&:checked {
		& + label {
			span {
				border-color: ${(p) => p.theme.color.primary};
				&:after {
					transform: scale(1);
					opacity: 1;
					background-color: ${(p) => p.theme.color.primary};
				}
			}
		}
	}
`;

export const RadioLabel = styled.label`
	cursor: pointer;
	display: flex;
	align-items: center;
	user-select: none;

	span {
		margin-left: 10px;
		position: relative;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		border: 2px solid ${(p) => p.theme.color.grayLight};
		transition: border 400ms ${(p) => p.theme.transition.function};

		&:after {
			position: absolute;
			top: 50%;
			left: 50%;
			margin-top: -4px;
			margin-left: -4px;
			width: 8px;
			height: 8px;
			content: '';
			opacity: 0;
			transform: scale(0);
			border-radius: 50%;
			transition: ${(p) => p.theme.transition.all};
		}
	}
`;

export const CheckboxLabel = styled.label`
	cursor: pointer;
	display: flex;
	align-items: center;
	user-select: none;

	span {
		margin-left: 10px;
		position: relative;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		border: 2px solid ${(p) => p.theme.color.grayLight};

		&:after {
			content: '';
			position: absolute;
			top: -2px;
			left: -2px;
			width: 18px;
			height: 18px;
			opacity: 0;
			transform: scale(0);
			background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+CjxzdmcKICAgeG1sbnM6ZGM9Imh0dHA6Ly9wdXJsLm9yZy9kYy9lbGVtZW50cy8xLjEvIgogICB4bWxuczpjYz0iaHR0cDovL2NyZWF0aXZlY29tbW9ucy5vcmcvbnMjIgogICB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiCiAgIHhtbG5zOnN2Zz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciCiAgIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIKICAgdmVyc2lvbj0iMS4xIgogICB2aWV3Qm94PSIwIDAgMSAxIgogICBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJ4TWluWU1pbiBtZWV0Ij4KICA8cGF0aAogICAgIGQ9Ik0gMC4wNDAzODA1OSwwLjYyNjc3NjcgMC4xNDY0NDY2MSwwLjUyMDcxMDY4IDAuNDI5Mjg5MzIsMC44MDM1NTMzOSAwLjMyMzIyMzMsMC45MDk2MTk0MSB6IE0gMC4yMTcxNTcyOSwwLjgwMzU1MzM5IDAuODUzNTUzMzksMC4xNjcxNTcyOSAwLjk1OTYxOTQxLDAuMjczMjIzMyAwLjMyMzIyMzMsMC45MDk2MTk0MSB6IgogICAgIGlkPSJyZWN0Mzc4MCIKICAgICBzdHlsZT0iZmlsbDojZmZmZmZmO2ZpbGwtb3BhY2l0eToxO3N0cm9rZTpub25lIiAvPgo8L3N2Zz4K');
			background-position: 50%;
			background-repeat: no-repeat;
			background-size: 12px;
			transition: ${(p) => p.theme.transition.all};
			border-radius: 50%;
		}
	}
`;

export const SelectWrapper = styled.div`
	position: relative;
	min-width: 80px;

	.icon {
		position: absolute;
		font-size: ${(p) => rem(8, p.theme.fontSize.base)};
		left: 5px;
		top: 50%;
		transform: translateY(-50%);
	}
`;
